import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ShieldCheck
} from 'lucide-react';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';

import { currenciesAPI } from '../services/api';
import { Currency, PaginationInfo } from '../types';
import Pagination from '../components/Pagination';
import { confirmToast } from '../components/confirmToast';
import { decryptData } from '../utils/encryption';

interface CurrencyFormData {
  name: string;
  is_default: boolean;
}

const initialPagination: PaginationInfo = {
  page: 1,
  limit: 10,
  total: 0,
  total_pages: 0
};

const emptyForm: CurrencyFormData = {
  name: '',
  is_default: false
};

const Currencies: React.FC = () => {
  const navigate = useNavigate();

  const permissionEncrypted = localStorage.getItem('permissions') || '';
  const userPermission = useMemo(() => {
    if (!permissionEncrypted) {
      return '';
    }
    try {
      return decryptData(permissionEncrypted);
    } catch (error) {
      console.error('Failed to decrypt permissions:', error);
      return '';
    }
  }, [permissionEncrypted]);

  const isAdmin = userPermission === 'admin' || userPermission === 'super_admin';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAdmin, navigate]);

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(initialPagination);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [formData, setFormData] = useState<CurrencyFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<{ name?: string }>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  const validateForm = () => {
    const errors: { name?: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'Currency name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitting(false);
    setEditingCurrency(null);
  };

  const fetchCurrencies = useCallback(
    async (page: number = pagination.page, search: string = searchTerm) => {
      if (!isAdmin) {
        return;
      }
      try {
        setLoading(true);
        const response = await currenciesAPI.getAll({
          page,
          limit: pagination.limit,
          search: search.trim() || undefined
        });
        setCurrencies(response.data || []);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination?.page ?? page,
          limit: response.pagination?.limit ?? prev.limit,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0
        }));
      } catch (error: any) {
        console.error('Failed to fetch currencies:', error);
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to fetch currencies';
        errorModal(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, pagination.limit, pagination.page, searchTerm]
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [searchInput, searchTerm]);

  useEffect(() => {
    fetchCurrencies(pagination.page, searchTerm);
  }, [fetchCurrencies, pagination.page, searchTerm]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleCreateCurrency = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        is_default: formData.is_default
      };
      const response = await currenciesAPI.create(payload);
      if (response.success) {
        toast.success(response.message || 'Currency created successfully');
      } else {
        errorModal(response.message || 'Failed to create currency');
      }
      setShowCreateModal(false);
      resetForm();
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchCurrencies(1, searchTerm);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to create currency';
      errorModal(errorMessage);
      console.error('Failed to create currency:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCurrency = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingCurrency) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        is_default: formData.is_default
      };
      const response = await currenciesAPI.update(editingCurrency.id, payload);
      if (response.success) {
        toast.success(response.message || 'Currency updated successfully');
      } else {
        errorModal(response.message || 'Failed to update currency');
      }
      setShowEditModal(false);
      resetForm();
      fetchCurrencies(pagination.page, searchTerm);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to update currency';
      errorModal(errorMessage);
      console.error('Failed to update currency:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCurrency = (currency: Currency) => {
    if (currency.is_default) {
      errorModal('Default currency cannot be deleted');
      return;
    }

    confirmToast(`Are you sure you want to delete ${currency.name}?`, async () => {
      try {
        const response = await currenciesAPI.delete(currency.id);
        if (response.success) {
          toast.success(response.message || 'Currency deleted successfully');
        } else {
          errorModal(response.message || 'Failed to delete currency');
        }
        fetchCurrencies(
          pagination.page > 1 && currencies.length === 1
            ? pagination.page - 1
            : pagination.page,
          searchTerm
        );
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to delete currency';
        errorModal(errorMessage);
        console.error('Failed to delete currency:', error);
      }
    });
  };

  const handleSetDefault = async (currency: Currency) => {
    if (currency.is_default) {
      return;
    }

    try {
      const response = await currenciesAPI.update(currency.id, {
        is_default: true
      });
      if (response.success) {
        toast.success(response.message || `${currency.name} is now the default currency`);
      } else {
        errorModal(response.message || 'Failed to update currency');
      }
      fetchCurrencies(pagination.page, searchTerm);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to update currency';
      errorModal(errorMessage);
      console.error('Failed to set default currency:', error);
    }
  };

  const startCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const startEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    setFormData({
      name: currency.name,
      is_default: currency.is_default
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    resetForm();
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Currencies</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage supported currencies and configure the default currency for the platform.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="btn-primary inline-flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Currency
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-xs">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search currencies"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading currencies...
                  </td>
                </tr>
              ) : currencies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">
                    No currencies found.
                  </td>
                </tr>
              ) : (
                currencies.map((currency) => (
                  <tr key={currency.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {currency.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {currency.is_default ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <ShieldCheck className="mr-1 h-3 w-3" />
                          Default
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(currency)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Set as default
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium space-x-1">
                      <button
                        onClick={() => startEdit(currency)}
                        className="inline-flex items-center px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                      >
                        <Edit className="mr-1 h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCurrency(currency)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={currency.is_default}
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          loading={loading}
          className="px-4 pb-4"
        />
      </div>

      {(showCreateModal || showEditModal) && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {showEditModal ? 'Edit Currency' : 'New Currency'}
              </h2>
              <button
                onClick={closeModals}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>
            <form
              onSubmit={showEditModal ? handleUpdateCurrency : handleCreateCurrency}
              className="px-6 py-5 space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, name: event.target.value }))
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., USD"
                  disabled={submitting}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Default Currency</h3>
                  <p className="text-xs text-gray-500">
                    Setting this currency as default will replace the current default currency.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={formData.is_default}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, is_default: event.target.checked }))
                    }
                    disabled={submitting}
                  />
                  <span
                    className={`w-11 h-6 flex items-center bg-gray-200 rounded-full p-1 transition ${
                      formData.is_default ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`bg-white w-4 h-4 rounded-full shadow transform transition ${
                        formData.is_default ? 'translate-x-5' : ''
                      }`}
                    ></span>
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : showEditModal ? 'Update Currency' : 'Create Currency'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Currencies;

